/**
 * As marcações de texto do WhatsApp.
 *
 * O atendente escreve `*teste*` no Studio e o cliente tem de ler **teste** — não
 * o asterisco. As regras são as do WhatsApp, porque é o que a equipe já tem no
 * dedo e o que o outro lado da conversa já renderiza.
 *
 * A saída é uma ÁRVORE, nunca HTML em texto. Quem desenha monta elementos a
 * partir dela, então uma mensagem com `<img onerror=...>` continua sendo só um
 * texto com sinais de menor e maior — não há caminho daqui para `innerHTML`.
 */

export type Trecho =
  | { tipo: "texto"; texto: string }
  | { tipo: "negrito" | "italico" | "riscado" | "mono"; filhos: Trecho[] }

export type Bloco =
  | { tipo: "paragrafo"; conteudo: Trecho[] }
  | { tipo: "citacao"; conteudo: Trecho[] }
  | { tipo: "lista"; ordenada: boolean; itens: Trecho[][] }

type TipoComFilhos = Exclude<Trecho, { tipo: "texto" }>["tipo"]

const MARCADORES: Record<string, TipoComFilhos> = {
  "*": "negrito",
  _: "italico",
  "~": "riscado",
}

/**
 * Um marcador só abre formatação quando "abraça" conteúdo de verdade.
 *
 * Sem isto, `2 * 3 = 6 * 2` viraria itálico do meio da conta, e um traço solto
 * no fim da frase comeria o resto. A regra: nada de espaço logo depois da
 * abertura nem logo antes do fechamento.
 */
function fechamentoValido(texto: string, inicio: number, marcador: string): number {
  if (texto[inicio + 1] === undefined || texto[inicio + 1] === " ") return -1
  let procurarDe = inicio + 2
  while (procurarDe <= texto.length) {
    const fim = texto.indexOf(marcador, procurarDe)
    if (fim === -1) return -1
    if (texto[fim - 1] !== " ") return fim
    procurarDe = fim + 1
  }
  return -1
}

/** O conteúdo de uma linha, já com negrito/itálico/riscado/mono aninhados. */
export function trechosDaLinha(linha: string): Trecho[] {
  const saida: Trecho[] = []
  let acumulado = ""
  let i = 0

  const despejarTexto = () => {
    if (acumulado) {
      saida.push({ tipo: "texto", texto: acumulado })
      acumulado = ""
    }
  }

  while (i < linha.length) {
    const caractere = linha[i]

    // Monoespaçado primeiro: o que está dentro dele é literal, inclusive `*`.
    if (linha.startsWith("```", i)) {
      const fim = linha.indexOf("```", i + 3)
      if (fim > i + 3) {
        despejarTexto()
        saida.push({
          tipo: "mono",
          filhos: [{ tipo: "texto", texto: linha.slice(i + 3, fim) }],
        })
        i = fim + 3
        continue
      }
    }

    const tipo = MARCADORES[caractere]
    if (tipo) {
      const fim = fechamentoValido(linha, i, caractere)
      if (fim > i + 1) {
        despejarTexto()
        saida.push({ tipo, filhos: trechosDaLinha(linha.slice(i + 1, fim)) })
        i = fim + 1
        continue
      }
    }

    acumulado += caractere
    i += 1
  }

  despejarTexto()
  return saida
}

const ITEM_MARCADO = /^\s*[-*]\s+(.*)$/
const ITEM_NUMERADO = /^\s*\d+[.)]\s+(.*)$/
const CITACAO = /^\s*>\s?(.*)$/

/**
 * O texto inteiro, em blocos.
 *
 * Linha a linha, porque é assim que o WhatsApp trata: tópico, citação e item
 * numerado são propriedades da LINHA, e negrito é do pedaço dentro dela.
 *
 * `* item` (asterisco e ESPAÇO) é tópico; `*item*` é negrito. É o espaço que
 * separa os dois, e é por isso que a lista é reconhecida antes do inline.
 */
export function blocosDoTexto(texto: string): Bloco[] {
  const linhas = String(texto ?? "").split("\n")
  const blocos: Bloco[] = []

  for (const linha of linhas) {
    const marcado = ITEM_MARCADO.exec(linha)
    const numerado = marcado ? null : ITEM_NUMERADO.exec(linha)

    if (marcado || numerado) {
      const ordenada = !!numerado
      const conteudo = trechosDaLinha((marcado ?? numerado)![1])
      const ultimo = blocos[blocos.length - 1]
      if (ultimo && ultimo.tipo === "lista" && ultimo.ordenada === ordenada) {
        ultimo.itens.push(conteudo)
      } else {
        blocos.push({ tipo: "lista", ordenada, itens: [conteudo] })
      }
      continue
    }

    const citacao = CITACAO.exec(linha)
    if (citacao) {
      blocos.push({ tipo: "citacao", conteudo: trechosDaLinha(citacao[1]) })
      continue
    }

    blocos.push({ tipo: "paragrafo", conteudo: trechosDaLinha(linha) })
  }

  return blocos
}
